// Recognizes errors raised by scripts the *browser* injected into the page,
// rather than by Bosko.
//
// Firefox-for-iOS and Brave-iOS inject reader-mode user scripts that touch
// `window.__firefox__`; the Instagram and Facebook in-app webviews inject a
// bridge (`sendDataToNative` / `sendPageHideMessage`) that reaches for
// `window.webkit.messageHandlers`. When one of those throws, the global
// `onerror` handler it hits is ours, so Sentry files the failure against Bosko:
//
//   BOSKO-FRONTEND-4J  undefined is not an object (window.__firefox__.reader)
//   BOSKO-FRONTEND-4H  Can't find variable: __firefox__
//   BOSKO-FRONTEND-4G  undefined is not an object (window.webkit.messageHandlers)
//
// They are unactionable: no Bosko code references any of those globals, and we
// cannot fix another vendor's user script.
//
// The signature is structural rather than a message denylist, so the next
// vendor's injected script is filtered too without a code change: every frame in
// the stack is the HTML document itself, and none is a Bosko bundle. Our own
// code always reports a `/vite/` chunk.
//
// Two deliberate limits. An event with no stack at all is kept — `captureMessage`
// calls look like that, and an unattributable event is not evidence of
// injection. And the inline theme guard in application.html.erb does report as
// the document, so a throw there would be dropped; accepted, because that
// failure announces itself immediately as an unthemed first paint.

// Vite serves the app bundle from /vite/assets/ in production.
const FIRST_PARTY_PATH = '/vite/';

function stackFrames(event) {
  const values = event?.exception?.values ?? [];
  return values.flatMap((value) => value?.stacktrace?.frames ?? []);
}

function withoutQuery(url) {
  return typeof url === 'string' && url ? url.split(/[?#]/)[0] : null;
}

function isFirstPartyFrame(frame) {
  return withoutQuery(frame?.filename)?.includes(FIRST_PARTY_PATH) ?? false;
}

export function isInjectedScriptError(event) {
  const frames = stackFrames(event);
  if (frames.length === 0) return false;
  if (frames.some(isFirstPartyFrame)) return false;

  const documentUrl = withoutQuery(event?.request?.url);
  if (!documentUrl) return false;

  return frames.every((frame) => withoutQuery(frame?.filename) === documentUrl);
}

// Firebase's optional web analytics cannot create an installation while
// offline. Its internal rejected promise escapes the SDK's init catch. Drop
// only that named failure; other Firebase and mixed exception groups stay.
export function isAnalyticsOfflineError(event) {
  const values = event?.exception?.values ?? [];
  return (
    values.length > 0 &&
    values.every(
      (value) =>
        value.type === 'FirebaseError' &&
        value.value?.includes('(installations/app-offline)'),
    )
  );
}
